import React, { createContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";

const MapContext = createContext();

// Handles global states and state updates for the entire Map app
// since the map and the filterbar need to be in sync, it was important to be able to easily access any state from any component

export const MapProvider = ({ children, isNewFood }) => {
	//
	const [locations, setLocations] = useState(null);
	// only one category in the menu shows its results at a time
	const [selectedCategory, setSelectedCateogry] =  useState(  isNewFood ? "food-building" :  "buildings");
	// location in popout modal
	const [selectedLocation, setSelectedLocation] = useState({});
	const [newFoodLocations, setNewFoodLocations] = useState([]);
	// string of name of location
	const [highlightedMenuItem, setHighlightedMenuItem] = useState("");
	// object of name and index
	const [highlightedPin, setHighlightedPin] = useState(null);
	// details panel
	const [showPopout, setShowPopout] = useState(false);
	// if there's an API error - show this error message
	const [errorMsg, setErrorMsg] = useState(null);

	const [highlightedLocation, setHighlightedLocation] = useState(null);

	// fetch all location data
	useEffect(() => {
		let baseUrl = window.origin;
		let reqUrl = `${baseUrl}/wp-json/cne/v1/locations${
			isNewFood ? "?newfood=1" : ""
		}`;
		const getResponse = async () => {
			try {
				const { data } = await axios.get(reqUrl);

				if (data.length <= 0) {
					setLocations([]);
					return;
				}

				// soring the data so that it is an  object: {category: [array of locations]}
				let resortedData;
				resortedData = data.reduce((locationsObj, location) => {
					// if the user forgets to add a category, ignore the location
					if (!location.category) return { ...locationsObj };

					locationsObj[location.category] = locationsObj[location.category]
						? [...locationsObj[location.category], location]
						: [location];

					return locationsObj;
				}, {});
				setLocations(resortedData);

				// creating a new array of locations for new food used for pins
				if (isNewFood) {
					const aggregatedData = data.reduce((acc, item) => {
						const { category, pins } = item;

						if (!acc[category]) {
							acc[category] = new Set();
						}

						pins.forEach((pin) => acc[category].add(pin.join(",")));

						return acc;
					}, {});

					const formattedArray = Object.keys(aggregatedData).map(
						(category) => ({
							category: category,
							pins: Array.from(aggregatedData[category], (pin) =>
								pin.split(",").map(Number)
							),
						})
					);
					setNewFoodLocations(formattedArray);
				}
			} catch (error) {
				setErrorMsg("Something went wrong. Please try again");
				throw new Error("Api request failed", { cause: error });
			}
		};

		getResponse();
	}, []);

	useEffect(() => {
		setShowPopout(false);
	}, [selectedCategory]);

	const handleCategory = (selection) => {
		const category = selection === selectedCategory ? null : selection;
		setSelectedCateogry(category);
		isNewFood && setHighlightedLocation(category);
	};

	const closePopout = () => {
		setShowPopout(false);
	};

	const handleHoverPin = (location = "", index = "all") => {
		// since each location might have multiple pins, we only want to highlight one pin
		// highlight just that pin
		// highlight that location in menu
		isNewFood? setHighlightedPin({ name: location?.category, index }) : setHighlightedPin({ name: location?.name, index });
		setHighlightedMenuItem(location.name);
		isNewFood && setHighlightedLocation(location.category);
	};

	const handleClickPin = (location, index = "all") => {
		// highlight that pin
		// show popout
		// keep pin highlighted until user hovers or selects something else
		setHighlightedPin({ name: location.name, index });
		setHighlightedMenuItem(location.name);
		setShowPopout(true);
		setSelectedLocation(location);
	};
	const handleHoverMenuItem = (location) => {
		// highlight that location in menu
		// highlight all instances of that location on map
		setHighlightedPin({ name: location, index: "all" });
		setHighlightedMenuItem(location);
	};
	const handleClickMenuItem = (location) => {
		// keep highlighted that location in menu
		// hightlight all instances of that location on map
		setHighlightedPin({ name: location.name, index: "all" });
		setHighlightedMenuItem(location.name);
		setSelectedLocation(location);
		setShowPopout(true);
	};

	const data = {
		locations,
		selectedCategory,
		selectedLocation,
		highlightedMenuItem,
		highlightedPin,
		showPopout,
		errorMsg,
		isNewFood,
		newFoodLocations,
		highlightedLocation
	};
	const actions = {
		handleCategory,
		closePopout,
		handleHoverPin,
		handleClickPin,
		handleHoverMenuItem,
		handleClickMenuItem,
	};

	return (
		<MapContext.Provider value={{ actions, data }}>
			{children}
		</MapContext.Provider>
	);
};

MapProvider.propTypes = {
	children: PropTypes.node,
	isNewFood: PropTypes.bool,
};
export default MapContext;
